import { Cmd, loop, Loop } from 'redux-loop';
import { UserState, UserAction } from './types';
import { UserActions, UserInternalActions } from './actions';
import { defaultState } from './consts';
import { UserModel, PortfolioModel, WishlistModel, ArtistModel, AddressModel } from 'models';
import { UserApiClient, ArtistApiClient, WaitlistApiClient, ItemApiClient, MarketplaceApiClient } from 'api';
import { ItemActiveMarketData } from 'models/Item/types';
import { UserOrderInfo } from 'models/User/User';

export enum UserStateErrors {
  PORTFOLIO_FETCH = 'Unable to fetch user portfolio',
  WISHLIST_FETCH = 'Unable to fetch user wishlist',
  FOLLOWING_ARTIST_FETCH = 'Unable to fetch user following artists',
  ACTIVE_MARKET_FETCH = 'Unable to fetch user active market data',
  ADD_ITEM_TO_PORTFOLIO = 'Unable to add item to user portfolio',
  REPLACE_ITEM_IN_PORTFOLIO = 'Unable to replace item in user portfolio',
  REMOVE_ITEM_FROM_PORTFOLIO = 'Unable to remove item from user portfolio',
  ADD_ITEM_TO_WISHLIST = 'Unable to add item to user wishlist',
  REMOVE_ITEM_FROM_WISHLIST = 'Unable to remove item from user wishlist',
  FOLLOW_ARTIST = 'Unable to follow artist',
  UNFOLLOW_ARTIST = 'Unable to unfollow artist',
  MIGRATE_IMAGE = 'Unable to migrate user image',
  CHANGE_PASSWORD = 'Unable to change user password',
  SEND_CONFIRMATION_EMAIL = 'Unable to send confirmation email',
  SEND_FORGOT_PASSWORD_EMAIL = 'Unable to process forgot password request',
  CONFIRM_EMAIL = 'Unable to confirm user email',
  JOIN_WAITLIST = 'Unable to join waitlist',
  SEND_CONTACT_REQUEST = 'Unable to send contact request',
  UPDATE_USER = 'Unable to update user data',
  DELETE_BID = 'Unable to delete item bid',
  DELETE_ASK = 'Unable to delete item ask',
  UPDATE_BID = 'Unable to update item bid',
  UPDATE_ASK = 'Unable to update item ask',
  ACTIVATE_SELLING = 'Unable to activate selling',
  GENERATE_SELLER_ONBOARDING_LINK = 'Unable to generate seller onboarding link',
  GENERATE_SELLER_DASHBOARD_LINK = 'Unable to generate seller dashboard link',
  ORDERS = 'Unable to fetch user orders',
  ADD_ADDRESS = 'Unable to add address'
}

export class UserHandlers {
  public static handleSetUser(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const user: UserModel = action.payload?.user || defaultState.user;
    const newState = {
      ...state,
      user
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserPortfolio(user.getPortfolioId())),
        Cmd.action(UserActions.getUserWishlist(user.getWishlistId())),
        Cmd.action(UserActions.getUserFollowingArtists()),
        Cmd.action(UserActions.getUserActiveMarket())
      ])
    );
  }

  public static handleClearUser(state: UserState): UserState {
    return {
      ...state,
      user: defaultState.user
    };
  }

  public static handleGetUserPortfolio(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        portfolio: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.getPortfolio, {
        args: [],
        successActionCreator: UserInternalActions.getUserPortfolioSuccess,
        failActionCreator: UserInternalActions.getUserPortfolioFailure,
      })
    );
  }

  public static handleGetUserPortfolioSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const portfolio: PortfolioModel = action.payload?.portfolio || state.user.portfolio;
    const newUser = UserModel.deepCopy(user);
    newUser.setPortfolio(portfolio);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        portfolio: false
      }
    };
  }

  public static handleGetUserPortfolioFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.PORTFOLIO_FETCH),
      loading: {
        ...state.loading,
        portfolio: false
      }
    };
  }

  public static handleGetUserWishlist(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        wishlist: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.getWishlist, {
        args: [],
        successActionCreator: UserInternalActions.getUserWishlistSuccess,
        failActionCreator: UserInternalActions.getUserWishlistFailure,
      })
    );
  }

  public static handleGetUserWishlistSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const wishlist: WishlistModel = action.payload?.wishlist || state.user.wishlist;
    const newUser = UserModel.deepCopy(user);
    newUser.setWishlist(wishlist);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        wishlist: false
      }
    };
  }

  public static handleGetUserWishlistFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.WISHLIST_FETCH),
      loading: {
        ...state.loading,
        wishlist: false
      }
    };
  }


  public static handleGetUserFollowingArtists(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        followingArtists: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.getFollowingArtists, {
        args: [],
        successActionCreator: UserInternalActions.getUserFollowingArtistsSuccess,
        failActionCreator: UserInternalActions.getUserFollowingArtistsFailure,
      })
    );
  }

  public static handleGetUserFollowingArtistsSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const artists: ArtistModel[] = action.payload?.artistList || [];
    const newUser = UserModel.deepCopy(user);
    newUser.setFollowingArtists(artists);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        followingArtists: false
      }
    };
  }

  public static handleGetUserFollowingArtistsFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.FOLLOWING_ARTIST_FETCH),
      loading: {
        ...state.loading,
        followingArtists: false
      }
    };
  }

  public static handleGetUserActiveMarket(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarket: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.getUserActiveMarket, {
        args: [],
        successActionCreator: UserInternalActions.getUserActiveMarketSuccess,
        failActionCreator: UserInternalActions.getUserActiveMarketFailure,
      })
    );
  }

  public static handleGetUserActiveMarketSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    let activeMarket: ItemActiveMarketData | undefined = action.payload?.activeMarket;
    const newUser = UserModel.deepCopy(user);
    newUser.setActiveMarket(activeMarket);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        activeMarket: false
      }
    };
  }

  public static handleGetUserActiveMarketFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ACTIVE_MARKET_FETCH),
      loading: {
        ...state.loading,
        activeMarket: false
      }
    };
  }

  public static handleAddUserPrimaryAddress(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        addressAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.addUserPrimaryAddress, {
        args: [payload?.address],
        successActionCreator: UserInternalActions.addPrimaryAddressSuccess,
        failActionCreator: UserInternalActions.addPrimaryAddressFailure,
      })
    );
  }

  public static handleAddUserPrimaryAddressSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const newUser = UserModel.deepCopy(user);
    let address: AddressModel | undefined = action.payload?.address;
    if (address) {
      newUser.setPrimaryAddress(address);
    }

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleAddUserPrimaryAddressFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ADD_ADDRESS),
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleAddUserSecondaryAddress(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        addressAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.addUserSecondaryAddress, {
        args: [payload?.address],
        successActionCreator: UserInternalActions.addSecondaryAddressSuccess,
        failActionCreator: UserInternalActions.addSecondaryAddressFailure,
      })
    );
  }

  public static handleAddUserSecondaryAddressSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const newUser = UserModel.deepCopy(user);
    let address: AddressModel | undefined = action.payload?.address;
    if (address) {
      newUser.addSecondaryAddress(address);
    }

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleAddUserSecondaryAddressFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ADD_ADDRESS),
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleRemoveUserSecondaryAddress(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        addressAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.removeUserSecondaryAddress, {
        args: [payload?.address],
        successActionCreator: UserInternalActions.removeSecondaryAddressSuccess,
        failActionCreator: UserInternalActions.removeSecondaryAddressFailure,
      })
    );
  }

  public static handleRemoveUserSecondaryAddressSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const newUser = UserModel.deepCopy(user);
    let address: AddressModel | undefined = action.payload?.address;
    if (address) {
      newUser.removeSecondaryAddress(address);
    }

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleRemoveUserSecondaryAddressFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ADD_ADDRESS),
      loading: {
        ...state.loading,
        addressAction: false
      }
    };
  }

  public static handleGetUserOrders(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        orders: true
      }
    };

    return loop(
      newState,
      Cmd.run(MarketplaceApiClient.getUserOrders, {
        args: [],
        successActionCreator: UserInternalActions.getUserOrdersSuccess,
        failActionCreator: UserInternalActions.getUserOrdersFailure,
      })
    );
  }

  public static handleGetUserOrdersSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    let orders: UserOrderInfo | undefined = action.payload?.orders;
    const newUser = UserModel.deepCopy(user);
    newUser.setOrders(orders);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        orders: false
      }
    };
  }

  public static handleGetUserOrdersFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ORDERS),
      loading: {
        ...state.loading,
        orders: false
      }
    };
  }

  public static handleFollowArtist(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        artistAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(ArtistApiClient.followArtist, {
        args: [payload?.artistId || ''],
        successActionCreator: UserInternalActions.followArtistSuccess,
        failActionCreator: UserInternalActions.followArtistFailure,
      })
    );
  }

  public static handleFollowArtistSuccess(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        artistAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserFollowingArtists())
      ])
    );
  }

  public static handleFollowArtistFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.FOLLOW_ARTIST),
      loading: {
        ...state.loading,
        artistAction: false
      }
    };
  }

  public static handleUnfollowArtist(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        artistAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(ArtistApiClient.unfollowArtist, {
        args: [payload?.artistId || ''],
        successActionCreator: UserInternalActions.unfollowArtistSuccess,
        failActionCreator: UserInternalActions.unfollowArtistFailure,
      })
    );
  }

  public static handleUnfollowArtistSuccess(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        artistAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserFollowingArtists())
      ])
    );
  }

  public static handleUnfollowArtistFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.UNFOLLOW_ARTIST),
      loading: {
        ...state.loading,
        artistAction: false
      }
    };
  }

  public static handleMigrateImage(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        imageAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.migrateImage, {
        args: [payload?.imageUrl || ''],
        successActionCreator: UserInternalActions.migrateImageSuccess,
        failActionCreator: UserInternalActions.migrateImageFailure,
      })
    );
  }

  public static handleMigrateImageSuccess(state: UserState, action: UserAction): UserState {
    const { user } = state;
    const newImageUrl: string = action.payload?.imageUrl || '';
    const newUser = UserModel.deepCopy(user);
    newUser.setImage(newImageUrl);

    return {
      ...state,
      user: newUser,
      loading: {
        ...state.loading,
        imageAction: false
      }
    };
  }

  public static handleMigrateImageFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.MIGRATE_IMAGE),
      loading: {
        ...state.loading,
        imageAction: false
      }
    };
  }

  public static handleChangePassword(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        passwordAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.changePassword, {
        args: [payload?.oldPassword || '', payload?.newPassword || ''],
        successActionCreator: UserInternalActions.changePasswordSuccess,
        failActionCreator: UserInternalActions.changePasswordFailure,
      })
    );
  }

  public static handleChangePasswordSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        passwordAction: false
      }
    };
  }

  public static handleChangePasswordFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.CHANGE_PASSWORD),
      loading: {
        ...state.loading,
        passwordAction: false
      }
    };
  }


  public static handleSendConfirmationEmail(state: UserState): Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        mailAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.sendConfirmationEmail, {
        args: [],
        successActionCreator: UserInternalActions.sendConfirmationEmailSuccess,
        failActionCreator: UserInternalActions.sendConfirmationEmailFailure,
      })
    );
  }

  public static handleSendConfirmationEmailSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleSendConfirmationEmailFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.SEND_CONFIRMATION_EMAIL),
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleSendForgotPasswordEmail(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        mailAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.sendUserForgotPasswordEmail, {
        args: [payload?.email || ''],
        successActionCreator: UserInternalActions.sendForgotPasswordEmailSuccess,
        failActionCreator: UserInternalActions.sendForgotPasswordEmailFailure,
      })
    );
  }

  public static handleSendForgotPasswordEmailSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleSendForgotPasswordEmailFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.SEND_FORGOT_PASSWORD_EMAIL),
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleConfirmEmail(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        confirmationAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.confirmUser, {
        args: [payload?.token || ''],
        successActionCreator: UserInternalActions.confirmUserSuccess,
        failActionCreator: UserInternalActions.confirmUserFailure,
      })
    );
  }

  public static handleConfirmEmailSuccess(state: UserState, action: UserAction): Loop<UserState, UserAction> | UserState {
    const { payload } = action;
    const user: UserModel | undefined = payload?.user;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        confirmationAction: false
      }
    };

    if (user) {
      return loop(
        newState,
        Cmd.list([
          Cmd.action(UserActions.setUser(user))
        ])
      );
    } else {
      return newState;
    }
  }

  public static handleConfirmEmailFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.CONFIRM_EMAIL),
      loading: {
        ...state.loading,
        confirmationAction: false
      }
    };
  }

  public static handleChangeForgottenPassword(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        passwordAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.changeUserForgottenPassword, {
        args: [payload?.token || '', payload?.newPassword || ''],
        successActionCreator: UserInternalActions.changeForgottenPasswordSuccess,
        failActionCreator: UserInternalActions.changeForgottenPasswordFailure,
      })
    );
  }

  public static handleChangeForgottenPasswordSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        passwordAction: false
      }
    };
  }

  public static handleChangeForgottenPasswordFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.CHANGE_PASSWORD),
      loading: {
        ...state.loading,
        passwordAction: false
      }
    };
  }


  public static handleJoinUserWaitlist(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        waitlistAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(WaitlistApiClient.joinUserWaitlist, {
        args: [payload?.email || ''],
        successActionCreator: UserInternalActions.joinUserWaitlistSuccess,
        failActionCreator: UserInternalActions.joinUserWaitlistFailure,
      })
    );
  }

  public static handleJoinUserWaitlistSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        waitlistAction: false
      }
    };
  }

  public static handleJoinUserWaitlistFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.JOIN_WAITLIST),
      loading: {
        ...state.loading,
        waitlistAction: false
      }
    };
  }

  public static handleSendInboundContactRequest(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        mailAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.sendInboundContactRequest, {
        args: [payload?.email || '', payload?.name || '', payload?.message || ''],
        successActionCreator: UserInternalActions.sendInboundContactRequestSuccess,
        failActionCreator: UserInternalActions.sendInboundContactRequestFailure,
      })
    );
  }

  public static handleSendInboundContactRequestSuccess(state: UserState): UserState {
    return {
      ...state,
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleSendInboundContactRequestFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.SEND_CONTACT_REQUEST),
      loading: {
        ...state.loading,
        mailAction: false
      }
    };
  }

  public static handleUpdateUserData(state: UserState, action: UserAction): Loop<UserState, UserAction> | UserState {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        updateAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(UserApiClient.updateUser, {
        args: [payload?.updatedData || {}],
        successActionCreator: UserInternalActions.updateUserDataSuccess,
        failActionCreator: UserInternalActions.updateUserDataFailure,
      })
    );
  }


  public static handleUpdateUserDataSuccess(state: UserState, action: UserAction): Loop<UserState, UserAction> | UserState {
    const { payload } = action;
    const user: UserModel | undefined = payload?.user;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        updateAction: false
      }
    };

    if (user) {
      return loop(
        newState,
        Cmd.list([
          Cmd.action(UserActions.setUser(user))
        ])
      );
    } else {
      return newState;
    }
  }

  public static handleUpdateUserDataFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.UPDATE_USER),
      loading: {
        ...state.loading,
        updateAction: false
      }
    };
  }

  public static handleDeleteItemBid(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: true,
      }
    };
    
    return loop(
      newState,
      Cmd.run(ItemApiClient.deleteItemBid, {
        args: [payload?.itemBidId || '', payload?.itemId || ''],
        successActionCreator: UserInternalActions.deleteItemBidSuccess,
        failActionCreator: UserInternalActions.deleteItemBidFailure,
      })
    );
  }

  public static handleDeleteItemBidSuccess(state: UserState): UserState | Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserActiveMarket())
      ])
    );
  }

  public static handleDeleteItemBidFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.DELETE_BID),
      loading: {
        ...state.loading,
        activeMarketAction: false,
      }
    };
  }

  public static handleDeleteItemAsk(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: true,
      }
    };
    return loop(
      newState,
      Cmd.run(ItemApiClient.deleteItemAsk, {
        args: [payload?.itemAskId || '', payload?.itemId || ''],
        successActionCreator: UserInternalActions.deleteItemAskSuccess,
        failActionCreator: UserInternalActions.deleteItemAskFailure,
      })
    );
  }

  public static handleDeleteItemAskSuccess(state: UserState): UserState | Loop<UserState, UserAction> {

    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserActiveMarket())
      ])
    );
  }

  public static handleDeleteItemAskFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.DELETE_ASK),
      loading: {
        ...state.loading,
        activeMarketAction: false,
      }
    };
  }

  public static handleUpdateItemBid(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: true,
      }
    };
    return loop(
      newState,
      Cmd.run(ItemApiClient.updateItemBid, {
        args: [payload?.itemBidId || '', payload?.itemId || '', payload?.bidData || {}],
        successActionCreator: UserInternalActions.updateItemBidSuccess,
        failActionCreator: UserInternalActions.updateItemBidFailure,
      })
    );
  }

  public static handleUpdateItemBidSuccess(state: UserState): UserState | Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserActiveMarket())
      ])
    );
  }

  public static handleUpdateItemBidFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.UPDATE_BID),
      loading: {
        ...state.loading,
        activeMarketAction: false,
      }
    };
  }

  public static handleUpdateItemAsk(state: UserState, action: UserAction): Loop<UserState, UserAction> {
    const { payload } = action;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: true,
      }
    };
    return loop(
      newState,
      Cmd.run(ItemApiClient.updateItemAsk, {
        args: [payload?.itemAskId || '', payload?.itemId || '', payload?.askData || {}],
        successActionCreator: UserInternalActions.updateItemAskSuccess,
        failActionCreator: UserInternalActions.updateItemAskFailure,
      })
    );
  }

  public static handleUpdateItemAskSuccess(state: UserState): UserState | Loop<UserState, UserAction> {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        activeMarketAction: false
      }
    };

    return loop(
      newState,
      Cmd.list([
        Cmd.action(UserActions.getUserActiveMarket())
      ])
    );
  }

  public static handleUpdateItemAskFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.UPDATE_ASK),
      loading: {
        ...state.loading,
        activeMarketAction: false,
      }
    };
  }

  public static handleActivateSelling(state: UserState): Loop<UserState, UserAction> | UserState {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        marketplaceAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(MarketplaceApiClient.activateSelling, {
        args: [],
        successActionCreator: UserInternalActions.activateSellingSuccess,
        failActionCreator: UserInternalActions.activateSellingFailure,
      })
    );
  }


  public static handleActivateSellingSuccess(state: UserState, action: UserAction): Loop<UserState, UserAction> | UserState {
    const { payload } = action;
    const user: UserModel | undefined = payload?.user;
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        marketplaceAction: false
      }
    };

    if (user) {
      return loop(
        newState,
        Cmd.list([
          Cmd.action(UserActions.setUser(user))
        ])
      );
    } else {
      return newState;
    }
  }

  public static handleActivateSellingFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.ACTIVATE_SELLING),
      loading: {
        ...state.loading,
        updateAction: false
      }
    };
  }

  public static handleGenerateSellerOnboardingLink(state: UserState): Loop<UserState, UserAction> | UserState {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        marketplaceAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(MarketplaceApiClient.generateSellerOnboardingLink, {
        args: [],
        successActionCreator: UserInternalActions.generateSellerOnboardingLinkSuccess,
        failActionCreator: UserInternalActions.generateSellerOnboardingLinkFailure,
      })
    );
  }


  public static handleGenerateSellerOnboardingLinkSuccess(state: UserState, action: UserAction): UserState {
    const { payload } = action;
    const onboardingLink: string = payload?.onboardingLink || '';
    return {
      ...state,
      sellerOnboardingLink: onboardingLink,
      loading: {
        ...state.loading,
        marketplaceAction: false
      }
    };
  }

  public static handleGenerateSellerOnboardingLinkFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.GENERATE_SELLER_ONBOARDING_LINK),
      loading: {
        ...state.loading,
        marketplaceAction: false
      }
    };
  }

  public static handleGenerateSellerDashboardLink(state: UserState): Loop<UserState, UserAction> | UserState {
    const newState = {
      ...state,
      loading: {
        ...state.loading,
        marketplaceAction: true
      }
    };

    return loop(
      newState,
      Cmd.run(MarketplaceApiClient.generateSellerDashboardLink, {
        args: [],
        successActionCreator: UserInternalActions.generateSellerDashboardLinkSuccess,
        failActionCreator: UserInternalActions.generateSellerDashboardLinkFailure,
      })
    );
  }


  public static handleGenerateSellerDashboardLinkSuccess(state: UserState, action: UserAction): UserState {
    const { payload } = action;
    const dashboardLink: string = payload?.dashboardLink || '';
    return {
      ...state,
      sellerDashboardLink: dashboardLink,
      loading: {
        ...state.loading,
        marketplaceAction: false
      }
    };
  }

  public static handleGenerateSellerDashboardLinkFailure(state: UserState): UserState {
    return {
      ...state,
      error: new Error(UserStateErrors.GENERATE_SELLER_DASHBOARD_LINK),
      loading: {
        ...state.loading,
        marketplaceAction: false
      }
    };
  }
}
